import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close, DeleteOutline, ExpandMore, Refresh } from "@mui/icons-material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useTheme } from "../../context/ThemeContext";
import agentService from "../../services/agentService";
import LearningRecordDeleteDialog from "./LearningRecordDeleteDialog";
import styles from "./LearningRecordsDialog.module.scss";

const DEFAULT_FILTERS = {
  learning_type: "",
  namespace: "",
  session_id: "",
  entity_id: "",
  entity_type: "",
  limit: 100,
};

const getErrorMessage = (error) =>
  error?.response?.data?.detail || error?.message || "Failed to load learning records";

const getRecordId = (record) =>
  record?.learning_id || record?.id || record?._id || record?.uuid || null;

const getKnowledgeId = (record) =>
  record?.knowledge_id || record?.id || record?._id || record?.uuid || null;

const getRecordType = (record) =>
  record?.learning_type || record?.type || record?.store || "n/a";

const getRecordNamespace = (record) => record?.namespace || record?.namespace_key || "n/a";

const tryParseJson = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return value;

  try {
    return JSON.parse(trimmed);
  } catch (_) {
    return value;
  }
};

const getRecordText = (record) => {
  const candidate =
    record?.summary ||
    record?.content ||
    record?.text ||
    record?.value ||
    record?.memory ||
    record?.payload;

  if (typeof candidate === "string") {
    const parsed = tryParseJson(candidate);
    if (parsed && typeof parsed === "object") {
      return (
        parsed?.summary ||
        parsed?.learning ||
        parsed?.description ||
        parsed?.context ||
        parsed?.title ||
        JSON.stringify(parsed)
      );
    }
    return candidate;
  }
  if (candidate && typeof candidate === "object") return JSON.stringify(candidate);

  return JSON.stringify(record || {});
};

const getKnowledgeText = (record) => {
  const parsedContent = tryParseJson(record?.content);
  if (parsedContent && typeof parsedContent === "object") {
    return (
      parsedContent?.learning ||
      parsedContent?.context ||
      parsedContent?.summary ||
      parsedContent?.description ||
      parsedContent?.title ||
      JSON.stringify(parsedContent)
    );
  }
  if (typeof parsedContent === "string" && parsedContent.trim()) return parsedContent;

  const parsedMetadata = tryParseJson(record?.metadata);
  if (parsedMetadata && typeof parsedMetadata === "object") {
    return JSON.stringify(parsedMetadata);
  }

  return "-";
};

const getDialogRecordLabel = (record) => {
  if (!record) return "Record";
  if (record?.knowledge_id) return "learned_knowledge";
  return getRecordType(record);
};

const parseDateValue = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "number") {
    const millis = value < 1e12 ? value * 1000 : value;
    const parsed = new Date(millis);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value !== "string") return null;

  const raw = value.trim();
  if (!raw) return null;

  if (/^\d+(\.\d+)?$/.test(raw)) {
    const numericValue = Number(raw);
    if (!Number.isNaN(numericValue)) {
      const millis = numericValue < 1e12 ? numericValue * 1000 : numericValue;
      const parsed = new Date(millis);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }
  }

  let normalized = raw.replace(" ", "T");
  normalized = normalized.replace(/\s+UTC$/i, "Z");
  normalized = normalized.replace(/([+-]\d{2})(\d{2})$/, "$1:$2");

  // Python datetime often comes without timezone; backend stores it in UTC.
  const hasTimezone = /(?:Z|[+-]\d{2}:\d{2})$/i.test(normalized);
  const isIsoWithoutTimezone =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,6})?)?$/.test(normalized);
  if (!hasTimezone && isIsoWithoutTimezone) {
    normalized = `${normalized}Z`;
  }

  const parsed = new Date(normalized);
  if (!Number.isNaN(parsed.getTime())) return parsed;

  const fallback = new Date(raw);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const formatDate = (value) => {
  const date = parseDateValue(value);
  if (!date) return "-";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const LearningRecordsDialog = ({ open, onClose, agentId }) => {
  const ACTIONS_COL_WIDTH = 220;
  const DATE_COL_WIDTH = 180;
  const { isDarkMode } = useTheme();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [knowledgeRecords, setKnowledgeRecords] = useState([]);
  const [knowledgeTotal, setKnowledgeTotal] = useState(0);
  const [deletingRecordId, setDeletingRecordId] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [recordViewOpen, setRecordViewOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [isKnowledgeExpanded, setIsKnowledgeExpanded] = useState(true);
  const [isRecordsExpanded, setIsRecordsExpanded] = useState(false);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);
  const tableContainerRef = useRef(null);

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDarkMode ? "dark" : "light",
          primary: {
            main: "#e36250",
          },
        },
      }),
    [isDarkMode]
  );

  const fetchRecords = useCallback(
    async (activeFilters) => {
      if (!agentId) return;

      const resolvedFilters = activeFilters || DEFAULT_FILTERS;
      const params = {
        limit: Number(resolvedFilters.limit) || 100,
      };

      ["learning_type", "namespace", "session_id", "entity_id", "entity_type"].forEach((key) => {
        const value = resolvedFilters[key];
        if (typeof value === "string" && value.trim()) {
          params[key] = value.trim();
        }
      });

      setLoading(true);
      setError("");

      try {
        const response = await agentService.getAgentLearningRecords(agentId, params);
        const nextRecords = Array.isArray(response?.records)
          ? response.records
          : Array.isArray(response)
          ? response
          : [];
        const nextKnowledgeRecords = Array.isArray(response?.learned_knowledge_records)
          ? response.learned_knowledge_records
          : [];

        setRecords(nextRecords);
        setTotal(typeof response?.total === "number" ? response.total : nextRecords.length);
        setKnowledgeRecords(nextKnowledgeRecords);
        setKnowledgeTotal(
          typeof response?.learned_knowledge_total === "number"
            ? response.learned_knowledge_total
            : nextKnowledgeRecords.length
        );
      } catch (err) {
        console.error("Error fetching learning records:", err);
        setError(getErrorMessage(err));
        setRecords([]);
        setTotal(0);
        setKnowledgeRecords([]);
        setKnowledgeTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [agentId]
  );

  useEffect(() => {
    if (!open) return;
    setFilters(DEFAULT_FILTERS);
    setIsKnowledgeExpanded(true);
    setIsRecordsExpanded(false);
    fetchRecords(DEFAULT_FILTERS);
  }, [open, agentId, fetchRecords]);

  const updateHorizontalIndicators = useCallback(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const hasOverflow = container.scrollWidth > container.clientWidth + 1;
    if (!hasOverflow) {
      setShowLeftFade(false);
      setShowRightFade(false);
      return;
    }

    const left = container.scrollLeft > 0;
    const right = container.scrollLeft + container.clientWidth < container.scrollWidth - 1;
    setShowLeftFade(left);
    setShowRightFade(right);
  }, []);

  useEffect(() => {
    if (!open || !isRecordsExpanded) return;
    const timer = setTimeout(() => updateHorizontalIndicators(), 0);
    window.addEventListener("resize", updateHorizontalIndicators);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updateHorizontalIndicators);
    };
  }, [open, isRecordsExpanded, records, loading, updateHorizontalIndicators]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleReset = () => {
    setSuccessMessage("");
    setFilters(DEFAULT_FILTERS);
    fetchRecords(DEFAULT_FILTERS);
  };

  const handleDeleteClick = (record) => {
    const learningId = getRecordId(record);
    if (!learningId) {
      setError("Unable to delete this row because learning_id is missing.");
      return;
    }
    setRecordToDelete(record);
  };

  const handleDeleteConfirm = async () => {
    if (!recordToDelete || !agentId) return;
    const learningId = getRecordId(recordToDelete);
    if (!learningId) return;

    setDeletingRecordId(learningId);
    setError("");
    setSuccessMessage("");

    try {
      await agentService.deleteAgentLearningRecord(agentId, learningId);
      setSuccessMessage("Learning record deleted successfully.");
      setRecordToDelete(null);
      fetchRecords(filters);
    } catch (err) {
      console.error("Error deleting learning record:", err);
      setError(getErrorMessage(err));
    } finally {
      setDeletingRecordId(null);
    }
  };

  const handleDeleteCancel = () => {
    if (deletingRecordId) return;
    setRecordToDelete(null);
  };

  const handleViewRecord = (record) => {
    setSelectedRecord(record);
    setRecordViewOpen(true);
  };

  const handleEditRecord = () => {
    setError("Record edit API is not available. Only list and delete are supported for records.");
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        className={styles.dialog}
        PaperProps={{
          className: styles.dialogPaper,
        }}
      >
        <DialogTitle className={styles.dialogTitle}>
          <Box className={styles.headerLeft}>
            <Typography variant="h6" className={styles.title}>
              Stored Learnings
            </Typography>
            <Chip size="small" label={`${total} records`} />
            <Chip size="small" variant="outlined" label={`${knowledgeTotal} learned knowledge`} />
          </Box>
          <IconButton onClick={onClose} size="small" className={styles.closeButton}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent className={styles.dialogContent}>
          {!agentId && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Save the agent first to view stored learnings.
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          <Box className={styles.filtersGrid}>
            <TextField
              size="small"
              label="Learning Type"
              value={filters.learning_type}
              onChange={(event) => handleFilterChange("learning_type", event.target.value)}
              disabled={!agentId || loading}
            />
            <TextField
              size="small"
              label="Namespace"
              value={filters.namespace}
              onChange={(event) => handleFilterChange("namespace", event.target.value)}
              disabled={!agentId || loading}
            />
            <TextField
              size="small"
              label="Session ID"
              value={filters.session_id}
              onChange={(event) => handleFilterChange("session_id", event.target.value)}
              disabled={!agentId || loading}
            />
            <TextField
              size="small"
              label="Entity ID"
              value={filters.entity_id}
              onChange={(event) => handleFilterChange("entity_id", event.target.value)}
              disabled={!agentId || loading}
            />
            <TextField
              size="small"
              label="Entity Type"
              value={filters.entity_type}
              onChange={(event) => handleFilterChange("entity_type", event.target.value)}
              disabled={!agentId || loading}
            />
            <FormControl size="small" disabled={!agentId || loading}>
              <InputLabel id="learning-record-limit-label">Limit</InputLabel>
              <Select
                labelId="learning-record-limit-label"
                label="Limit"
                value={filters.limit}
                onChange={(event) => handleFilterChange("limit", Number(event.target.value))}
              >
                {[25, 50, 100, 200, 500].map((limit) => (
                  <MenuItem key={limit} value={limit}>
                    {limit}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button
              variant="contained"
              onClick={() => {
                setSuccessMessage("");
                fetchRecords(filters);
              }}
              disabled={!agentId || loading}
              startIcon={loading ? <CircularProgress size={14} /> : <Refresh />}
            >
              Apply
            </Button>
            <Button variant="outlined" onClick={handleReset} disabled={!agentId || loading}>
              Reset
            </Button>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box className={styles.loadingState}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                Loading learning records...
              </Typography>
            </Box>
          ) : records.length === 0 && knowledgeRecords.length === 0 ? (
            <Box className={styles.emptyState}>
              <Typography variant="body2" color="text.secondary">
                No learning records or learned knowledge found for the selected filters.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              <Accordion
                expanded={isKnowledgeExpanded}
                onChange={(_, expanded) => setIsKnowledgeExpanded(expanded)}
                disableGutters
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2">Learned Knowledge</Typography>
                    <Chip size="small" label={`${knowledgeTotal} records`} />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {knowledgeRecords.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                      No learned knowledge records found for the selected filters.
                    </Typography>
                  ) : (
                    <TableContainer className={styles.tableContainer}>
                      <Table size="small" stickyHeader sx={{ minWidth: 1000 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Namespace</TableCell>
                            <TableCell>Preview</TableCell>
                            <TableCell
                              sx={{
                                minWidth: DATE_COL_WIDTH,
                                maxWidth: DATE_COL_WIDTH,
                                whiteSpace: "nowrap",
                                position: "sticky",
                                right: ACTIONS_COL_WIDTH,
                                zIndex: 5,
                                backgroundColor: "background.paper",
                              }}
                            >
                              Created
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                minWidth: ACTIONS_COL_WIDTH,
                                maxWidth: ACTIONS_COL_WIDTH,
                                whiteSpace: "nowrap",
                                position: "sticky",
                                right: 0,
                                zIndex: 6,
                                backgroundColor: "background.paper",
                              }}
                            >
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {knowledgeRecords.map((record, index) => {
                            const knowledgeId = getKnowledgeId(record) || `knowledge-${index}`;

                            return (
                              <TableRow hover key={knowledgeId}>
                                <TableCell>{record?.name || "-"}</TableCell>
                                <TableCell>{getRecordNamespace(record)}</TableCell>
                                <TableCell>
                                  <Typography className={styles.previewText}>{getKnowledgeText(record)}</Typography>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    minWidth: DATE_COL_WIDTH,
                                    maxWidth: DATE_COL_WIDTH,
                                    whiteSpace: "nowrap",
                                    position: "sticky",
                                    right: ACTIONS_COL_WIDTH,
                                    zIndex: 3,
                                    backgroundColor: "background.paper",
                                  }}
                                >
                                  {formatDate(record?.created_at || record?.timestamp)}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    minWidth: ACTIONS_COL_WIDTH,
                                    maxWidth: ACTIONS_COL_WIDTH,
                                    whiteSpace: "nowrap",
                                    position: "sticky",
                                    right: 0,
                                    zIndex: 4,
                                    backgroundColor: "background.paper",
                                  }}
                                >
                                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                    <Button size="small" onClick={() => handleViewRecord(record)}>
                                      View
                                    </Button>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={isRecordsExpanded}
                onChange={(_, expanded) => setIsRecordsExpanded(expanded)}
                disableGutters
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2">Learning Records</Typography>
                    <Chip size="small" label={`${total} records`} />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {records.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                      No learning records found for the selected filters.
                    </Typography>
                  ) : (
                    <Box className={styles.tableWrapper}>
                      {showLeftFade && <Box className={`${styles.scrollFade} ${styles.leftFade}`} />}
                      {showRightFade && (
                        <>
                          <Box className={`${styles.scrollFade} ${styles.rightFade}`} />
                          <Typography variant="caption" className={styles.scrollHint}>
                            Scroll to see more columns
                          </Typography>
                        </>
                      )}
                      <TableContainer
                        ref={tableContainerRef}
                        className={styles.tableContainer}
                        onScroll={updateHorizontalIndicators}
                      >
                        <Table size="small" stickyHeader sx={{ minWidth: 1240 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Type</TableCell>
                              <TableCell>Namespace</TableCell>
                              <TableCell>Preview</TableCell>
                              <TableCell>Session</TableCell>
                              <TableCell>Entity</TableCell>
                              <TableCell
                                sx={{
                                  minWidth: DATE_COL_WIDTH,
                                  maxWidth: DATE_COL_WIDTH,
                                  whiteSpace: "nowrap",
                                  position: "sticky",
                                  right: ACTIONS_COL_WIDTH,
                                  zIndex: 5,
                                  backgroundColor: "background.paper",
                                }}
                              >
                                Created
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  minWidth: ACTIONS_COL_WIDTH,
                                  maxWidth: ACTIONS_COL_WIDTH,
                                  whiteSpace: "nowrap",
                                  position: "sticky",
                                  right: 0,
                                  zIndex: 6,
                                  backgroundColor: "background.paper",
                                }}
                              >
                                Actions
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {records.map((record, index) => {
                              const recordId = getRecordId(record) || `record-${index}`;
                              const sessionId = record?.session_id || "-";
                              const entityText =
                                record?.entity_id || record?.entity_type
                                  ? `${record?.entity_type || "entity"}:${record?.entity_id || "-"}`
                                  : "-";

                              return (
                                <TableRow hover key={recordId}>
                                  <TableCell>{getRecordType(record)}</TableCell>
                                  <TableCell>{getRecordNamespace(record)}</TableCell>
                                  <TableCell>
                                    <Typography className={styles.previewText}>
                                      {getRecordText(record)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{sessionId}</TableCell>
                                  <TableCell>{entityText}</TableCell>
                                  <TableCell
                                    sx={{
                                      minWidth: DATE_COL_WIDTH,
                                      maxWidth: DATE_COL_WIDTH,
                                      whiteSpace: "nowrap",
                                      position: "sticky",
                                      right: ACTIONS_COL_WIDTH,
                                      zIndex: 3,
                                      backgroundColor: "background.paper",
                                    }}
                                  >
                                    {formatDate(record?.created_at || record?.timestamp)}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      minWidth: ACTIONS_COL_WIDTH,
                                      maxWidth: ACTIONS_COL_WIDTH,
                                      whiteSpace: "nowrap",
                                      position: "sticky",
                                      right: 0,
                                      zIndex: 4,
                                      backgroundColor: "background.paper",
                                    }}
                                  >
                                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                      <Button size="small" onClick={() => handleViewRecord(record)}>
                                        View
                                      </Button>
                                      <Tooltip title="Record edit endpoint is not available in current APIs">
                                        <span>
                                          <Button size="small" onClick={handleEditRecord}>
                                            Edit
                                          </Button>
                                        </span>
                                      </Tooltip>
                                      <Button
                                        size="small"
                                        color="error"
                                        onClick={() => handleDeleteClick(record)}
                                        disabled={Boolean(deletingRecordId)}
                                        startIcon={<DeleteOutline fontSize="small" />}
                                      >
                                        Delete
                                      </Button>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            </Stack>
          )}
        </DialogContent>

        <DialogActions className={styles.dialogActions}>
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={recordViewOpen}
        onClose={() => setRecordViewOpen(false)}
        maxWidth="md"
        fullWidth
        className={styles.dialog}
        PaperProps={{
          className: styles.recordDialogPaper,
        }}
      >
        <DialogTitle className={styles.dialogTitle}>
          <Box className={styles.headerLeft}>
            <Typography variant="h6" className={styles.title}>
              Learning Record Details
            </Typography>
            <Chip
              size="small"
              label={getDialogRecordLabel(selectedRecord)}
            />
          </Box>
          <IconButton onClick={() => setRecordViewOpen(false)} size="small" className={styles.closeButton}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <pre className={styles.jsonBlock}>
            {JSON.stringify(selectedRecord || {}, null, 2)}
          </pre>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setRecordViewOpen(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <LearningRecordDeleteDialog
        open={Boolean(recordToDelete)}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        record={recordToDelete}
        isDeleting={Boolean(deletingRecordId)}
      />
    </ThemeProvider>
  );
};

export default LearningRecordsDialog;
